import { useEffect, useState } from 'react';

// Org-wide display governance (enterprise-polish item 10): timezone/locale/
// currency every date/number/money in the live app formats through, instead
// of each browser's own locale/timezone with no indication either way (see
// backend migration 0038 + GET/PATCH /api/org/display). A plain module-level
// singleton rather than React context - most formatting (core/utils.js's
// fmtMoney/formatCell, shared/Cellv.jsx) happens in plain functions outside
// any component tree, and threading prefs through every one of those call
// sites would be a much larger refactor than the value here justifies.
// App.jsx fetches this once, right after the session resolves, well before
// any data view actually mounts - see its own effect for the fetch.
const DEFAULT_PREFS = { timezone: 'UTC', locale: null, currency: 'USD' };
let _prefs = DEFAULT_PREFS;
const _listeners = new Set();

export function setDisplayPrefs(patch) {
  _prefs = { ..._prefs, ...patch };
  _listeners.forEach((fn) => fn(_prefs));
}

export function getDisplayPrefs() {
  return _prefs;
}

// For the few components that DO need to reactively update the moment
// prefs load/change (the header's timezone badge, the settings form itself)
// rather than just reading the value once at format time.
export function useDisplayPrefs() {
  const [prefs, setPrefs] = useState(_prefs);
  useEffect(() => {
    const fn = (p) => setPrefs(p);
    _listeners.add(fn);
    return () => _listeners.delete(fn);
  }, []);
  return prefs;
}

// Today's date (YYYY-MM-DD) on the ORG's clock: the same "today" the backend's `__today__` resolves (db.org_display_timezone), so a
// "to date" range or a "this week" anchor agrees with the server's. Not `new Date().toISOString()` (UTC's date) and not
// getFullYear/getMonth/getDate (the browser's): either is a different day for part of every day in a zone far from UTC, which is how
// PeriodPicker's "Year to date" ended a day early for a Singapore org after midnight (ci run 229, on a UTC+8 runner). `now` is for tests.
export function todayInOrg(now = new Date()) {
  const zone = _prefs.timezone || 'UTC';
  let parts;
  try {
    parts = new Intl.DateTimeFormat('en-US', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
  } catch {
    return now.toISOString().slice(0, 10);   // an unrecognised zone string: UTC, the documented default
  }
  const get = (type) => parts.find((p) => p.type === type).value;
  return `${get('year')}-${get('month')}-${get('day')}`;
}

// A short, stable abbreviation for a banner/badge - "EST"/"PST"/"UTC" rather
// than the full IANA zone name, which is too long for a persistent header
// chip. Falls back to the zone name itself if the runtime can't resolve a
// short form (very old browsers, or an unrecognised zone string).
export function tzAbbrev(timezone) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'short' }).formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value || timezone;
  } catch {
    return timezone;
  }
}
